<template>
  <div>
    <vx-card title="Venue Details" class="mb-base">
      <template slot="actions">
        <vs-button
          size="small"
          icon-pack="feather"
          icon="icon-edit"
          class="mr-2"
          :to="{ name: 'edit-venue', params: { venueId: $route.params.id } }"
          >Edit</vs-button
        >
        <vs-button
          size="small"
          type="border"
          color="danger"
          icon-pack="feather"
          icon="icon-trash"
          @click="confirmDeleteRecord"
          >Delete</vs-button
        >
      </template>
      
      <div class="venue-details-content">
        <div class="venue-card-header">
          <div class="venue-title-section">
            <h2 class="venue-main-title">{{ venue.name }}</h2>
            <div class="venue-meta">
              <span class="venue-display-label">{{ venue.displayName }}</span>
            </div>
          </div>
          <div v-if="revolutLocationId" class="venue-revolut-location">
            <span class="venue-revolut-label">Revolut Location ID</span>
            <span class="venue-revolut-value">{{ revolutLocationId }}</span>
          </div>
        </div>

        <div class="venue-stats-grid">
          <div class="stat-card">
            <div class="stat-icon">
              <feather-icon icon="LayersIcon" svgClasses="w-4 h-4" />
            </div>
            <div class="stat-content">
              <div class="stat-label">Venue Type</div>
              <div class="stat-value">{{ venue.typeName }}</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <feather-icon icon="CalendarIcon" svgClasses="w-4 h-4" />
            </div>
            <div class="stat-content">
              <div class="stat-label">Max Booking</div>
              <div class="stat-value">{{ venue.maxBooking }}</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <feather-icon icon="PhoneIcon" svgClasses="w-4 h-4" />
            </div>
            <div class="stat-content">
              <div class="stat-label">Telephone</div>
              <div class="stat-value">{{ venue.telephone }}</div>
            </div>
          </div>
        </div>

        <div class="venue-address-card">
          <div class="address-card-header">
            <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Address Details</h5>
          </div>
          <div class="address-one-liner">
            <span class="address-text">
              {{ venue.houseNumber ? venue.houseNumber + ', ' : '' }}{{ venue.street }}, {{ venue.city }}, {{ venue.postalCode }}, {{ venue.country }}
            </span>
          </div>
        </div>

        <div class="venue-address-card mt-4">
          <div class="address-card-header">
            <feather-icon icon="MapIcon" svgClasses="w-4 h-4 text-primary mr-2" />
            <h5>Nearby Destinations</h5>
          </div>
          <nearby-destinations
            :value="nearbyDestinations"
            :readonly="true"
          />
        </div>
      </div>
    </vx-card>

    <vx-card>
      <div class="portals-list-container">
        <div class="portals-header">
          <div class="portals-title-section">
            <feather-icon icon="ListIcon" svgClasses="w-5 h-5 text-primary mr-2" />
            <h4 class="portals-title">Portals List</h4>
          </div>
          <vs-button
            @click="setVenueIndex(venueId)"
            type="filled"
            color="success"
            size="small"
            icon-pack="feather"
            icon="icon-plus"
            >Add Portal</vs-button
          >
        </div>
        <div class="portals-table-wrapper">
          <vs-table :data="vPortals" pagination :max-items="itemsPerPage">
            <template slot="thead">
              <vs-th>Type</vs-th>
              <vs-th>Role</vs-th>
              <vs-th>Username</vs-th>
              <vs-th>Password</vs-th>
              <vs-th>Device ID</vs-th>
              <vs-th>Status</vs-th>
              <vs-th>Actions</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr
                :data="ptr"
                :key="indexptr"
                v-for="(ptr, indexptr) in data"
                class="portal-row"
              >
                <vs-td :data="ptr.type">
                  <span class="portal-type-badge" :class="'type-' + (ptr.type || 'default')">
                    {{ ptr.type ? ptr.type.charAt(0).toUpperCase() + ptr.type.slice(1) : "---" }}
                  </span>
                </vs-td>
                <vs-td :data="ptr.role">
                  <span class="portal-role">{{ ptr.role ? ptr.role.charAt(0).toUpperCase() + ptr.role.slice(1) : "---" }}</span>
                </vs-td>
                <vs-td>
                  <span class="portal-username">{{ ptr.user && ptr.type != "device" ? ptr.user.email : "---" }}</span>
                </vs-td>
                <vs-td>
                  <span class="portal-password">{{ ptr.user && ptr.type != "device" ? ptr.user.api_password : "---" }}</span>
                </vs-td>
                <vs-td :data="ptr.device_id">
                  <span class="portal-device-id">{{ ptr.device_id || "---" }}</span>
                </vs-td>
                <vs-td>
                  <div v-if="ptr.type == 'device' && ptr.user" class="status-indicator">
                    <div
                      class="status-dot"
                      :class="ptr.user.status == 'online' ? 'status-online' : 'status-offline'"
                    ></div>
                    <span class="status-text">{{ ptr.user.status == 'online' ? 'Online' : 'Offline' }}</span>
                  </div>
                  <span v-else class="text-grey">---</span>
                </vs-td>
                <vs-td class="portal-actions">
                  <div class="action-buttons">
                    <vs-button
                      @click.stop="editPortal(ptr)"
                      type="border"
                      size="small"
                      icon-pack="feather"
                      icon="icon-edit"
                      class="action-btn edit-btn"
                      >Edit</vs-button
                    >
                    <vs-button
                      @click.stop="confirmDeletePortal(ptr)"
                      type="border"
                      color="danger"
                      size="small"
                      icon-pack="feather"
                      icon="icon-trash"
                      class="action-btn delete-btn"
                      >Delete</vs-button
                    >
                  </div>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>
      </div>
      <vs-popup title="Select portal " :active.sync="portalChoicePopup">
            <portal-types @selectPortalType="setPortalType" />

            <!-- this popup is used in case of online and delivery selection  -->
            <vs-popup
              :title="'Add ' + (portal && portal.type ? portal.type : '') + ' Portal'"
              :active.sync="onlinePortalPopup"
            >
              <online-delivery-portal
                :role-options="roleOptions"
                @addParentPortal="updatePortalUI"
              />
            </vs-popup>

            <vs-popup title="Add New Device" :active.sync="devicePortalPopup">
              <online-delivery-portal
                :role-options="roleOptions"
                @addParentPortal="updatePortalUI"
              />
            </vs-popup>

            <vs-popup title="Add API Portal" :active.sync="apiPortalPopup">
              <api-portal @addParentPortal="updatePortalUI" />
            </vs-popup>

            <vs-popup
              class="text-center"
              title="QR Booker"
              :active.sync="qrPopup"
            >
              <div class="append-text btn-addon">
                <vs-button
                  type="border"
                  @click="generateQR"
                  class="whitespace-no-wrap"
                  >Generate QR Code</vs-button
                >
                <vs-button
                  type="border"
                  @click="removeQR"
                  class="ml-2 whitespace-no-wrap"
                  >Remove QR Code</vs-button
                >
              </div>

              <img
                class="mt-4"
                v-if="venue_qr_exists"
                :src="main_domain + '/qrcodes/venue-' + venueIndex + '.svg'"
                alt=""
              />
            </vs-popup>
          </vs-popup>
          <vs-popup
            :title="'Edit ' + (portal && portal.type ? portal.type : '') + ' Portal'"
            :active.sync="editOnlinePortalPopup"
          >
            <online-delivery-portal
              :role-options="roleOptions"
              @updateParentPortal="updatePortalUI"
            />
          </vs-popup>

    </vx-card>
          <vs-popup
            :title="'Edit Device Settings'"
            :active.sync="editDevicePortalPopup"
          >
            <portal-settings
              :key="portal && portal.id ? portal.id : 'device-settings'"
              :allowed-capabilities="allowedCapabilities"
              :terminal-id-value="editingTerminalId"
              @updateConfigParent="updatePortalSettings"
            ></portal-settings>
          </vs-popup>
  </div>
</template>
<script>
import Vue from 'vue'
import { _show, _delete,   _deletePortal,
  _updatePortalSettings,
  _generateQR,
  _removeQR,
  _placesList } from "@/services/Venue";

import { _update_user_status } from "@/services/User";


import { MAIN_DOMAIN } from '@/environment'

import vSelect from "vue-select";
import { Validator } from "vee-validate";

import PortalTypes from "./PortalTypesPopup";
import OnlineDeliveryPortal from "./OnlineDelivery";
import PortalSettings from "./PortalSettings";
import ApiPortal from "./ApiPortal";
import NearbyDestinations from "./NearbyDestinations.vue";
import modulePortal from "@/store/portal/modulePortalManagement.js";
import { _permissions } from "@/services/Portal";

const dict = {
  custom: {
    portal_username: {
      required: "Please enter username",
    },
    portal_password: {
      required: "Please enter password",
    },
    portal_role: {
      required: "Please select role",
    },
    portal_device: {
      required: "Please enter device address",
    },
  },
};

Validator.localize("en", dict);

export default {
  components: {
    vSelect,
    PortalTypes,
    OnlineDeliveryPortal,
    PortalSettings,
    ApiPortal,
    NearbyDestinations,
  },
  mounted() {
    this.$store.registerModule("portalModule", modulePortal);
    this.venueId = this.$route.params.id;
    this.getVenue(this.venueId);
  },
  beforeDestroy: function () {
    this.$store.unregisterModule("portalModule");
  },
  computed: {
    portal: {
      get() {
        return this.$store.state.portalModule || {};
      },
    },
    itemColor() {
      return (obj) => {
        let style_obj = {}

        obj.style.forEach(p => { style_obj[p] = obj.color })

        return style_obj
      }
    },
    revolutLocationId() {
      const venue = this.venue || {}
      const value = venue.revolutLocationId || venue.revolut_location_id
      if (value === null || value === undefined || value === '') return null
      return value
    },
  },

  data() {
    return {
      venueId: "",
      venue: {},
      nearbyDestinations: {
        popularPlaces: [],
        hotelsNearby: [],
        cityCenter: [],
      },
      vPortals: [],
      itemsPerPage: 10,

      main_domain : MAIN_DOMAIN,
      portalChoicePopup: false,
      onlinePortalPopup: false,
      devicePortalPopup: false,
      apiPortalPopup: false,
      qrPopup: false,
      editOnlinePortalPopup: false,
      editDevicePortalPopup: false,
      editingTerminalId: null,
      roleOptions: [
        { label: "Basic", value: "basic" },
        { label: "Reception", value: "reception" },
        { label: "Kiosk", value: "kiosk" },
      ],

      venue_qr_exists: false,
      allowedCapabilities: null
    };
  },
  methods: {
    normalizeDestinations(source = {}) {
      const mapPlace = (item) => {
        if (
          typeof item === "number" ||
          (typeof item === "string" && item !== "" && !isNaN(item))
        ) {
          return {
            id: Number(item),
            name: "",
            address: "",
            latitude: "",
            longitude: "",
            placeId: "",
            type: "",
          };
        }
        return {
          id: item.id || null,
          name: item.name || "",
          address: item.address || item.street || "",
          latitude: item.latitude || "",
          longitude: item.longitude || "",
          placeId: item.placeId || item.place_id || "",
          type: item.type || "",
        };
      };
      const toArray = (value) => {
        if (Array.isArray(value)) return value.map(mapPlace);
        if (value && typeof value === "object") return [mapPlace(value)];
        return [];
      };
      return {
        popularPlaces: toArray(
          source.popularPlaces || source.popular_places || []
        ),
        hotelsNearby: toArray(
          source.hotelsNearby || source.hotels_nearby || []
        ),
        cityCenter: toArray(source.cityCenter || source.city_center || []),
      };
    },
    hydrateDestinationNames() {
      const companyId =
        (this.venue && this.venue.companyId) ||
        this.$store.getters["company/getCompanyID"];
      if (!companyId) return;

      const needsHydration = ["popularPlaces", "hotelsNearby", "cityCenter"].some(
        (key) =>
          (this.nearbyDestinations[key] || []).some(
            (item) => item.id && !item.name
          )
      );
      if (!needsHydration) return;

      const typeMap = {
        popularPlaces: "popular",
        hotelsNearby: "hotel",
        cityCenter: "city_center",
      };

      Object.keys(typeMap).forEach((key) => {
        _placesList(typeMap[key], companyId)
          .then((response) => {
            const list =
              (response.data &&
                response.data.obj &&
                (response.data.obj.list || response.data.obj)) ||
              [];
            if (!Array.isArray(list)) return;

            const byId = {};
            list.forEach((place) => {
              if (place && place.id != null) byId[place.id] = place;
            });

            this.$set(
              this.nearbyDestinations,
              key,
              (this.nearbyDestinations[key] || []).map((item) => {
                const full = byId[item.id];
                if (!full) return item;
                return {
                  ...item,
                  name: full.name || item.name,
                  address: full.address || item.address,
                  latitude: full.latitude || item.latitude,
                  longitude: full.longitude || item.longitude,
                  placeId: full.placeId || item.placeId,
                  type: full.type || item.type,
                };
              })
            );
          })
          .catch(() => {});
      });
    },
    getVenue(venueId) {
      _show(venueId)
        .then((response) => {
          this.venue = response.data.obj.venue || {};
          this.vPortals = response.data.obj.portals;
          const venueData = this.venue;
          const objData = response.data.obj || {};
          if (!this.venue.revolutLocationId && !this.venue.revolut_location_id) {
            this.$set(
              this.venue,
              'revolutLocationId',
              objData.revolutLocationId || objData.revolut_location_id || null
            );
          }
          this.nearbyDestinations = this.normalizeDestinations({
            popularPlaces:
              venueData.popularPlaces ||
              venueData.popular_places ||
              objData.popularPlaces ||
              objData.popular_places,
            hotelsNearby:
              venueData.hotelsNearby ||
              venueData.hotels_nearby ||
              objData.hotelsNearby ||
              objData.hotels_nearby,
            cityCenter:
              venueData.cityCenter ||
              venueData.city_center ||
              objData.cityCenter ||
              objData.city_center,
          });
          this.hydrateDestinationNames();

          // Extract allowed_capabilities from response
          if (response.data.obj.allowed_capabilities) {
            // Handle if it's a JSON string or already an array
            if (typeof response.data.obj.allowed_capabilities === 'string') {
              try {
                this.allowedCapabilities = JSON.parse(response.data.obj.allowed_capabilities);
              } catch (error) {
                console.error('Error parsing allowed_capabilities:', error);
                this.allowedCapabilities = null;
              }
            } else if (Array.isArray(response.data.obj.allowed_capabilities)) {
              this.allowedCapabilities = response.data.obj.allowed_capabilities;
            } else {
              this.allowedCapabilities = null;
            }
          } else {
            this.allowedCapabilities = null;
          }
        })
        .catch((error) => {
          this.$vs.notify({
            title: error.response.data.responseTitle,
            text: error.response.data.responseMessage,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger",
          });
        });
    },

    confirmDeleteRecord() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm Delete`,
        text: `You are about to delete "${this.venue.displayName}"`,
        accept: this.deleteRecord,
        acceptText: "Delete",
      });
    },

    deleteRecord() {
      _delete(this.venueId)
        .then((response) => {
          this.$vs.notify({
            title: response.data.responseTitle,
            text: response.data.responseMessage,
            iconPack: "feather",
            icon: "icon-check-circle",
            color: "success",
          });
          this.$router.push({ name: "list-venues" });
        })
        .catch((error) => {
          this.$vs.notify({
            title: error.response.data.responseTitle,
            text: error.response.data.responseMessage,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger",
          });
        });

      /* UnComment below lines for enabling true flow if deleting user */
      // this.$store.dispatch("userManagement/removeRecord", this.user_data.id)
      //   .then(()   => { this.$router.push({name:'app-user-list'}); this.showDeleteSuccess() })
      //   .catch(err => { console.error(err)       })
    },

    setVenueIndex(venueID) {
      this.portalChoicePopup = true;
      this.venueIndex = venueID;
      this.venue_qr_exists = this.venue.qr_exists == 1 ? true:false
    },

    setPortalType(portalType) {

      this.errors.clear();
      if (portalType == "online") {
        this.portal.role = { label: "Basic", value: "basic" };
        this.onlinePortalPopup = true;
      }
      else if (portalType == "device") {
        this.devicePortalPopup = true;
      }
      else if (portalType == "delivery") {
        this.portal.role = { label: "Delivery", value: "delivery" };
        this.onlinePortalPopup = true;
      }
      else if (portalType == "api") {
        this.apiPortalPopup = true;
      }
      else if (portalType == "qr-booker")
      {
        this.qrPopup = true;
      }
      this.$store.dispatch("portalModule/updatePortal", {
        type: portalType,
        venue_id: this.venueIndex,
        role: this.portal.role,
      });
      this.$store.dispatch("portalModule/setPortalPermission", {
        permissions: "",
      });

    },

    listen() {
      this.$echo
        .join("taxipoint")
        .here((users) => {
          const onlineUsers = users.filter(
            (u) =>
              u.id !== this.$store.state.AppActiveUser.uid &&
              u.status === "offline"
          );
          onlineUsers.map((o) => {
            let model = this.allPortals.find((p) => p.user_id == o.id);
            if (model) {
              let v_idx = this.venues.findIndex((v) => v.id === model.venue_id);
              let selectedVenuePortals = this.venues[v_idx].portals;
              let p_idx = selectedVenuePortals.findIndex(
                (p) => p.user_id === o.id
              );
              let v_portal = selectedVenuePortals[p_idx];
              if (v_portal) {
                v_portal.user.status = "online";
              }
              this.venues[v_idx].portals.splice(p_idx, 1, v_portal);
            }
          });
        })
        .joining((user) => {
         this.portalJoined(user);
        })
        .leaving((user) => {
          this.portalLeft(user);
        });
    },

    portalJoined(user) {
      let p_idx = this.vPortals.findIndex((p) => p.user_id === user.id);
      let v_portal = this.vPortals[p_idx]; // get portal to update
      if (v_portal) {
        v_portal.user.status = "online";
        _update_user_status(v_portal.user.id, { status: "online" }); // calling api
        this.vPortals.splice(p_idx, 1, v_portal);
      }
    },

    portalLeft(user) {
      let p_idx = this.vPortals.findIndex((p) => p.user_id === user.id);
      let v_portal = this.vPortals[p_idx]; // get portal to update
      if (v_portal) {
        v_portal.user.status = "offline";
        _update_user_status(v_portal.user.id, { status: "offline" }); // calling api
        this.vPortals.splice(p_idx, 1, v_portal);
      }
    },

    updatePortalUI(response, calltype = "add") {
      let localPortal = response.data.obj.portal;
      if (calltype == "edit") {
        this.updatePortalRow(localPortal);
        this.editOnlinePortalPopup = false;
        this.apiPortalPopup = false;
      } else {
        this.vPortals.push(localPortal);

        this.onlinePortalPopup = false;
        this.devicePortalPopup = false;
        this.apiPortalPopup = false;
        this.portalChoicePopup = false;
      }
      this.$store.dispatch("portalModule/resetPortal");
      this.editingTerminalId = null;
    },

    editPortal(val) {

      if (val.type == "api") {
        // get portal permissions
        _permissions(val.id).then((res) => {
          this.$store.dispatch(
            "portalModule/setPortalPermission",
            res.data.obj
          );
        });
      }
      this.errors.clear();
      let localRole = {};
      switch (val.role) { // val.role
        case "delivery":
          localRole = { label: "delivery", value: "delivery" };
          break;
        case "basic":
          localRole = { label: "Basic", value: "basic" };
          break;
        case "reception":
          localRole = { label: "Reception", value: "reception" };
          break;
        case "kiosk":
          localRole = { label: "Kiosk", value: "kiosk" };
          break;
        case "device":
          localRole = { label: "Device", value: "device" };
          break;
        default:
          break;
      }

      // Ensure backward compatibility: initialize payment_modes if not exists
      const portalConfig = val.config || {}
      if (!portalConfig.payment_modes) {
        portalConfig.payment_modes = []
      }

      this.$store.dispatch("portalModule/updatePortal", {
        id: val.id,
        device_id: val.device_id,
        username: val.user.api_username,
        password: val.user.api_password,
        type: val.type,
        duration: val.duration,
        venue_id: val.venue_id,
        config: portalConfig,
        role: localRole,
        customer_id: val.user.customer_id,
        capabilities: val.capabilities,
        registration_key: val.registration_key,
      });

      this.editingTerminalId = (val.terminal_id === undefined || val.terminal_id === null) ? null : val.terminal_id;

      if (val.type == "device") {
        this.venueIndex = val.venue_id;
        this.editDevicePortalPopup = true;
      } else if (val.type == "api") {
        this.apiPortalPopup = true;
      } else {
        this.editOnlinePortalPopup = true;
      }
    },

    confirmDeletePortal(val) {
      const portalType = val.type ? val.type.charAt(0).toUpperCase() + val.type.slice(1) : 'Portal';
      const portalName = val.device_id || (val.user && val.user.api_username) || `${portalType} Portal`;
      
      // Temporarily suppress Vuesax clientWidth error (non-critical library issue)
      const originalErrorHandler = Vue.config.errorHandler;
      Vue.config.errorHandler = (err, vm, info) => {
        // Suppress the specific clientWidth error from Vuesax dialog
        if (err && err.message && typeof err.message === 'string' && err.message.includes('clientWidth')) {
          return; // Suppress the error silently
        }
        // Call original error handler for other errors
        if (originalErrorHandler) {
          originalErrorHandler(err, vm, info);
        }
      };
      
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm Delete`,
        text: `Are you sure you want to delete this ${portalType.toLowerCase()} portal "${portalName}"? This action cannot be undone.`,
        accept: () => {
          // Restore original error handler after a short delay
          setTimeout(() => {
            Vue.config.errorHandler = originalErrorHandler;
            this.deletePortal(val);
          }, 200);
        },
        acceptText: "Delete",
        cancelText: "Cancel",
        cancel: () => {
          // Restore original error handler on cancel
          setTimeout(() => {
            Vue.config.errorHandler = originalErrorHandler;
          }, 200);
        }
      });
    },

    deletePortal(val) {
      this.$vs.loading();
      _deletePortal(val.venue_id, val.id)
        .then(
          (response) => {
            this.$vs.loading.close();
            const selectedPortalIndex = this.vPortals.findIndex(
              (p) => p.id == val.id
            );
            this.vPortals.splice(
              selectedPortalIndex,
              1
            );
            this.$vs.notify({
              title: response.data.responseTitle,
              text: response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success",
            });
          },
          (error) => {
            this.$vs.loading.close();
            this.$vs.notify({
              title: error.response.data.responseTitle,
              text: error.response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger",
            });
          }
        )
        .catch((error) => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: error.response.data.responseTitle,
            text: error.response.data.responseMessage,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger",
          });
        });
    },

    updatePortalSettings(response) {
      this.updatePortalRow(response.data.obj.portal);
      this.editDevicePortalPopup = false;
      this.venueIndex = -1;
      this.$store.dispatch("portalModule/resetPortal");
      this.editingTerminalId = null;
    },

    updatePortalRow(localPortal) {
      // const selectedVenueIndex = this.venues.findIndex(
      //   (v) => v.id == localPortal.venue_id
      // );
      // const selectedVen = this.venues[selectedVenueIndex];
      const selectedPortalIndex = this.vPortals.findIndex(
        (p) => p.id == localPortal.id
      );
      this.$set(
        this.vPortals,
        selectedPortalIndex,
        localPortal
      );
    },

    generateQR() {
      this.$vs.loading();
      _generateQR(this.venueIndex)
        .then(
          (response) => {
            this.$vs.loading.close();
            this.venue_qr_exists =  true
            this.venue.qr_exists = 1;
          },
          (error) => {
            this.$vs.loading.close();
            this.$vs.notify({
              title: error.response.data.responseTitle,
              text: error.response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger",
            });
          }
        )
        .catch((error) => {
          this.$vs.loading.close();
          this.$vs.notify({
            text: error.message,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger",
          });
        });
    },

    removeQR() {
      this.$vs.loading();
      _removeQR(this.venueIndex)
        .then(
          (response) => {
            this.$vs.loading.close();
            this.venue_qr_exists =  false
            this.venue.qr_exists = 0


          },
          (error) => {
            this.$vs.loading.close();
            this.$vs.notify({
              title: error.response.data.responseTitle,
              text: error.response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger",
            });
          }
        )
        .catch((error) => {
          this.$vs.loading.close();
          this.$vs.notify({
            text: error.message,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger",
          });
        });
    },
  },
};
</script>

<style scoped>
.vx-card >>> .vx-card__actions {
  display: flex;
  align-items: center;
}

.vx-card >>> .vx-card__actions .vs-button {
  margin-left: 0.5rem;
}

/* Venue Details Styles */
.venue-details-content {
  padding: 0;
}

.venue-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  border-radius: 0.375rem;
  border: 1px solid #e4e4e4;
}

.venue-title-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.venue-main-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 700;
  color: #22292f;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.venue-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.venue-display-label {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  border-radius: 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(115, 103, 240, 0.2);
}

.venue-revolut-location {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
  text-align: right;
}

.venue-revolut-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #626262;
}

.venue-revolut-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #22292f;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  word-break: break-all;
}

.venue-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.stat-card:hover {
  border-color: #7367f0;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.1);
}

.stat-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9ff;
  border-radius: 0.25rem;
  flex-shrink: 0;
}

.stat-icon >>> svg {
  width: 16px;
  height: 16px;
  color: #7367f0 !important;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-label {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0.125rem;
}

.stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #22292f;
  word-break: break-word;
}

.venue-address-card {
  padding: 0.875rem 1rem;
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 0.375rem;
}

.address-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.625rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.address-card-header h5 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #22292f;
}

.address-card-header >>> svg {
  width: 16px;
  height: 16px;
}

.address-one-liner {
  padding-top: 0.25rem;
}

.address-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #22292f;
  line-height: 1.5;
  word-break: break-word;
}

@media (max-width: 768px) {
  .venue-card-header {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
  }

  .venue-stats-grid,
  .address-fields {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .venue-name-title {
    font-size: 1.25rem;
  }
}

/* Portals List Styles */
.portals-list-container {
  padding: 0;
}

.portals-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e4e4e4;
  margin-bottom: 0;
}

.portals-title-section {
  display: flex;
  align-items: center;
}

.portals-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #22292f;
}

.portals-table-wrapper {
  padding: 0;
}

.portals-table-wrapper >>> .vs-table {
  border-collapse: separate;
  border-spacing: 0 0.5rem;
}

.portals-table-wrapper >>> .vs-table--thead th {
  padding: 0.875rem 1rem;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  border: none;
}

.portals-table-wrapper >>> .vs-table tbody tr {
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.portals-table-wrapper >>> .vs-table tbody tr:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #7367f0;
}

.portals-table-wrapper >>> .vs-table tbody td {
  padding: 0.875rem 1rem;
  border: none;
  vertical-align: middle;
}

.portal-type-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: capitalize;
}

.portal-type-badge.type-online {
  background: rgba(40, 199, 111, 0.1);
  color: #28c76f;
}

.portal-type-badge.type-device {
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
}

.portal-type-badge.type-api {
  background: rgba(255, 159, 67, 0.1);
  color: #ff9f43;
}

.portal-type-badge.type-delivery {
  background: rgba(234, 84, 85, 0.1);
  color: #ea5455;
}

.portal-type-badge.type-default {
  background: #f0f1f2;
  color: #6e6b7b;
}

.portal-role {
  font-size: 0.875rem;
  font-weight: 500;
  color: #22292f;
}

.portal-username,
.portal-password,
.portal-device-id {
  font-size: 0.875rem;
  color: #22292f;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.status-online {
  background: #28c76f;
  box-shadow: 0 0 0 2px rgba(40, 199, 111, 0.2);
}

.status-dot.status-offline {
  background: #ea5455;
  box-shadow: 0 0 0 2px rgba(234, 84, 85, 0.2);
}

.status-text {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #22292f;
}

.portal-actions {
  min-width: 160px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem 0.75rem !important;
  font-size: 0.8125rem !important;
}

@media (max-width: 768px) {
  .venue-card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .venue-revolut-location {
    align-items: flex-start;
    text-align: left;
  }

  .portals-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }

  .portals-table-wrapper >>> .vs-table tbody td {
    padding: 0.75rem 0.5rem;
    font-size: 0.8125rem;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
